export const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/** Resolve a media URL: S3 URLs (https://...) are used as-is, local paths are prefixed with API_URL. */
export function resolveMediaUrl(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
}

/** Format FastAPI error detail (string or array of validation errors) into a readable message. */
function formatApiError(detail, fallback = 'Request failed') {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((e) => e.msg || e.message || JSON.stringify(e)).filter(Boolean);
    return msgs.length ? msgs.join('. ') : fallback;
  }
  return fallback;
}

export const api = {
  async request(endpoint, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      const message = formatApiError(error.detail, response.status === 401 ? 'Unauthorized' : response.status === 403 ? 'Access denied' : 'An error occurred');
      throw new Error(message);
    }

    if (response.status === 204) return null;
    return response.json();
  },

  // Auth
  getGoogleAuthUrl(gender) {
    const base = `${API_URL}/auth/google`;
    if (gender) return `${base}?gender=${encodeURIComponent(gender)}`;
    return base;
  },

  async register(email, password, name, gender, age) {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name, gender, age }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Registration failed' }));
      throw new Error(error.detail || 'Registration failed');
    }

    const data = await response.json();
    return data;
  },

  async requestOtp(phone_number) {
    const response = await fetch(`${API_URL}/auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phone_number }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'OTP Request failed' }));
      throw new Error(error.detail || 'OTP Request failed');
    }

    return response.json();
  },

  async login(email, password) {
    const response = await fetch(`${API_URL}/auth/login/json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(error.detail || 'Login failed');
    }

    return response.json();
  },

  async verifyOtp(phone_number, otp_code) {
    const response = await fetch(`${API_URL}/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ phone_number, otp_code }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'OTP Verification failed' }));
      throw new Error(error.detail || 'OTP Verification failed');
    }

    return response.json();
  },

  async logout() {
    try {
      await fetch(`${API_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // Ignore network errors during logout
    }
  },

  async getMe() {
    return this.request('/auth/me');
  },

  async getUserAnalytics() {
    return this.request('/auth/me/analytics');
  },
  async updateProfile(data) {
    return this.request('/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async verifyEmail(token) {
    return this.request(`/auth/verify-email?token=${token}`);
  },

  async uploadProfilePicture(formData) {
    const response = await fetch(`${API_URL}/auth/me/profile-picture`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to upload profile picture' }));
      throw new Error(error.detail || 'Failed to upload profile picture');
    }

    return response.json();
  },

  // Shops
  async getShops(category = null) {
    const url = category ? `/shops?category=${category}` : '/shops';
    return this.request(url);
  },

  async getShop(id) {
    return this.request(`/shops/${id}`);
  },

  /** Update shop (super_admin: full ShopUpdate; shop_manager: social_links + cancellation_cutoff_hours for own shop). */
  async updateShop(shopId, data) {
    return this.request(`/shops/${shopId}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  async uploadShopLogo(shopId, formData) {
    const response = await fetch(`${API_URL}/shops/${shopId}/logo`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to upload shop logo' }));
      throw new Error(error.detail || 'Failed to upload shop logo');
    }
    return response.json();
  },

  async deleteShopLogo(shopId) {
    const response = await fetch(`${API_URL}/shops/${shopId}/logo`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to delete shop logo' }));
      throw new Error(error.detail || 'Failed to delete shop logo');
    }
    return response.json();
  },

  // Branches (with distance sorting)
  async getBranches(category = null, shopId = null, lat = null, lng = null) {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (shopId != null) params.append('shop_id', shopId);
    if (lat != null) params.append('lat', lat);
    if (lng != null) params.append('lng', lng);
    const url = params.toString() ? `/branches?${params}` : '/branches';
    return this.request(url);
  },

  async getBranch(id) {
    return this.request(`/branches/${id}`);
  },

  async createBranch(data) {
    return this.request('/branches', { method: 'POST', body: JSON.stringify(data) });
  },

  async updateBranch(id, data) {
    return this.request(`/branches/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },

  async uploadBranchLogo(branchId, formData) {
    const response = await fetch(`${API_URL}/branches/${branchId}/logo`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to upload branch logo' }));
      throw new Error(error.detail || 'Failed to upload branch logo');
    }
    return response.json();
  },

  async deleteBranchLogo(branchId) {
    const response = await fetch(`${API_URL}/branches/${branchId}/logo`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to delete branch logo' }));
      throw new Error(error.detail || 'Failed to delete branch logo');
    }
    return response.json();
  },

  async deleteBranch(id) {
    return this.request(`/branches/${id}`, { method: 'DELETE' });
  },

  async getBranchServices(branchId) {
    return this.request(`/branches/${branchId}/services`);
  },

  /** For booking flow: standalone services + bundles (services in a bundle are not offered standalone). */
  async getBranchServiceOptions(branchId) {
    return this.request(`/branches/${branchId}/service-options`);
  },

  async getBranchAvailability(branchId, serviceIdOrIds, date, lang = null) {
    const params = new URLSearchParams({ date });
    if (Array.isArray(serviceIdOrIds) && serviceIdOrIds.length > 0) {
      serviceIdOrIds.forEach(id => params.append('service_ids', id));
    } else if (serviceIdOrIds) {
      params.append('service_id', serviceIdOrIds);
    }
    if (lang) params.set('lang', lang);
    return this.request(`/branches/${branchId}/availability?${params}`);
  },

  async getTopRatedShops(limit = 6) {
    return this.request(`/shops/top-rated?limit=${limit}`);
  },

  async getTopRatedBranches(limit = 6) {
    return this.request(`/branches/top-rated?limit=${limit}`);
  },

  async getTopRatedArtists(limit = 6) {
    return this.request(`/artists/top-rated?limit=${limit}`);
  },

  async rateBooking(bookingId, rating) {
    return this.request(`/bookings/${bookingId}/rating`, {
      method: 'PUT',
      body: JSON.stringify({ rating }),
    });
  },

  async cancelBooking(bookingId) {
    return this.request(`/bookings/${bookingId}/cancel`, {
      method: 'PUT',
    });
  },

  async getShopServices(shopId) {
    return this.request(`/shops/${shopId}/services`);
  },

  async getAvailability(shopId, serviceIdOrIds, date) {
    const params = new URLSearchParams({ date });
    // Support both single serviceId (backward compat) and array of serviceIds
    if (Array.isArray(serviceIdOrIds) && serviceIdOrIds.length > 0) {
      serviceIdOrIds.forEach(id => params.append('service_ids', id));
    } else if (serviceIdOrIds) {
      params.append('service_id', serviceIdOrIds);
    }
    return this.request(`/shops/${shopId}/availability?${params}`);
  },

  // Artists (shopId and/or branchId optional)
  async getArtists(shopId = null, branchId = null) {
    const params = new URLSearchParams();
    if (shopId != null) params.append('shop_id', shopId);
    if (branchId != null) params.append('branch_id', branchId);
    const q = params.toString();
    return this.request(q ? `/artists?${q}` : '/artists');
  },

  async getArtist(id) {
    return this.request(`/artists/${id}`);
  },

  // Products
  async getProducts(shopId = null, category = null, skip = null, limit = null) {
    const params = new URLSearchParams();
    if (shopId) params.append('shop_id', shopId);
    if (category) params.append('category', category);
    if (skip != null) params.append('skip', skip);
    if (limit != null) params.append('limit', limit);
    const url = params.toString() ? `/products?${params}` : '/products';
    return this.request(url);
  },

  async getProduct(id) {
    return this.request(`/products/${id}`);
  },

  async getRecommendedProducts({ context = 'cart', productId = null, limit = 8, excludeIds = [] } = {}) {
    const params = new URLSearchParams();
    params.append('context', context);
    if (productId != null) params.append('product_id', productId);
    if (limit != null) params.append('limit', limit);
    if (Array.isArray(excludeIds) && excludeIds.length > 0) {
      params.append('exclude_ids', excludeIds.join(','));
    }
    return this.request(`/products/recommendations?${params.toString()}`);
  },

  async trackProductEvent({ productId, eventType, context = 'unknown' }) {
    return this.request('/products/events', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, event_type: eventType, context }),
    });
  },

  // Wishlist / Save for later
  async getWishlist() {
    return this.request('/me/wishlist');
  },
  async addToWishlist(productId) {
    return this.request('/me/wishlist', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, quantity: 1 }),
    });
  },
  async removeFromWishlist(productId) {
    return this.request(`/me/wishlist/${productId}`, { method: 'DELETE' });
  },
  async getSavedForLater() {
    return this.request('/me/save-for-later');
  },
  async addToSavedForLater(productId, quantity = 1) {
    return this.request('/me/save-for-later', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId, quantity }),
    });
  },
  async updateSavedForLater(productId, quantity) {
    return this.request(`/me/save-for-later/${productId}`, {
      method: 'PATCH',
      body: JSON.stringify({ quantity }),
    });
  },
  async removeFromSavedForLater(productId) {
    return this.request(`/me/save-for-later/${productId}`, { method: 'DELETE' });
  },

  // Bookings
  async getBookings() {
    return this.request('/bookings');
  },

  async createBooking(bookingData) {
    return this.request('/bookings', {
      method: 'POST',
      body: JSON.stringify(bookingData),
    });
  },

  // Orders
  async createOrder(orderData) {
    return this.request('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  async getMyOrders() {
    return this.request('/orders/me');
  },

  async getOrder(id) {
    return this.request(`/orders/${id}`);
  },

  // Payments (Paymob)
  async initiatePayment(entityType, entityId) {
    return this.request('/payments/initiate', {
      method: 'POST',
      body: JSON.stringify({ entity_type: entityType, entity_id: entityId }),
    });
  },

  async getPaymentStatus(entityType, entityId) {
    return this.request(`/payments/status/${entityType}/${entityId}`);
  },

  // Public booking flow uses getShopServices + getAvailability + createBooking with service_id, artist_id, seat_id

  // Admin (super_admin / project owner)
  async getAdminDashboard() {
    return this.request('/admin/dashboard');
  },
  async getAdminAnalytics(shopId = null, branchId = null) {
    const params = new URLSearchParams();
    if (shopId) params.append('shop_id', shopId);
    if (branchId) params.append('branch_id', branchId);
    const qs = params.toString();
    return this.request(qs ? `/admin/analytics?${qs}` : '/admin/analytics');
  },
  async downloadAdminFinancialReport(shopId = null, branchId = null) {
    const params = new URLSearchParams();
    if (shopId) params.append('shop_id', shopId);
    if (branchId) params.append('branch_id', branchId);
    const qs = params.toString();
    const url = `${API_URL}/admin/financial-report/csv${qs ? `?${qs}` : ''}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(formatApiError(err.detail, 'Failed to download report'));
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition');
    const match = cd?.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] || `financial_report_${new Date().toISOString().slice(0, 10)}.csv`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  },
  async getAdminFinancialReport(dateFrom, dateTo, shopId = null, branchId = null) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (shopId) params.append('shop_id', shopId);
    if (branchId) params.append('branch_id', branchId);
    const qs = params.toString();
    return this.request(`/admin/financial-report${qs ? `?${qs}` : ''}`);
  },
  async getAdminUsers(role) {
    const url = role ? `/admin/users?role=${role}` : '/admin/users';
    return this.request(url);
  },
  async createAdminUser(data) {
    return this.request('/admin/users', { method: 'POST', body: JSON.stringify(data) });
  },
  async getAdminUser(id) {
    return this.request(`/admin/users/${id}`);
  },
  async updateAdminUser(id, data) {
    return this.request(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminUser(id) {
    return this.request(`/admin/users/${id}`, { method: 'DELETE' });
  },
  async getAdminShops() {
    return this.request('/admin/shops');
  },
  async createAdminShop(data) {
    return this.request('/admin/shops', { method: 'POST', body: JSON.stringify(data) });
  },
  async getAdminShop(id) {
    return this.request(`/admin/shops/${id}`);
  },
  async updateAdminShop(id, data) {
    return this.request(`/admin/shops/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminShop(id) {
    return this.request(`/admin/shops/${id}`, { method: 'DELETE' });
  },
  async getAdminBookings() {
    return this.request('/admin/bookings');
  },
  async updateAdminBooking(id, data) {
    return this.request(`/admin/bookings/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminBooking(id) {
    return this.request(`/admin/bookings/${id}`, { method: 'DELETE' });
  },
  async createAdminWalkInBooking(data) {
    return this.request('/admin/bookings', { method: 'POST', body: JSON.stringify(data) });
  },

  // Admin mirror endpoints (branches, artists, photos, attendance, premium ops, product categories)
  async getAdminBranches(shopId = null) {
    const q = shopId != null ? `?shop_id=${shopId}` : '';
    return this.request(`/admin/branches${q}`);
  },
  async getAdminAssignableBranchManagers() {
    return this.request('/admin/assignable-branch-managers');
  },
  async getAdminArtists({ shopId = null, branchId = null } = {}) {
    const params = new URLSearchParams();
    if (shopId != null) params.append('shop_id', shopId);
    if (branchId != null) params.append('branch_id', branchId);
    const qs = params.toString();
    return this.request(qs ? `/admin/artists?${qs}` : '/admin/artists');
  },
  async createAdminArtist(data) {
    return this.request('/admin/artists', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateAdminArtist(id, data) {
    return this.request(`/admin/artists/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminArtist(id) {
    return this.request(`/admin/artists/${id}`, { method: 'DELETE' });
  },
  async uploadAdminArtistPhoto(artistId, formData) {
    const response = await fetch(`${API_URL}/admin/artists/${artistId}/photo`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to upload photo' }));
      throw new Error(error.detail || 'Failed to upload photo');
    }
    return response.json();
  },
  async deleteAdminArtistPhoto(artistId) {
    const response = await fetch(`${API_URL}/admin/artists/${artistId}/photo`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to delete photo' }));
      throw new Error(error.detail || 'Failed to delete photo');
    }
    return response.json();
  },
  async uploadAdminProductPhoto(productId, formData) {
    const response = await fetch(`${API_URL}/admin/products/${productId}/photo`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to upload photo' }));
      throw new Error(error.detail || 'Failed to upload photo');
    }
    return response.json();
  },
  async deleteAdminProductPhoto(productId) {
    const response = await fetch(`${API_URL}/admin/products/${productId}/photo`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to delete photo' }));
      throw new Error(error.detail || 'Failed to delete photo');
    }
    return response.json();
  },
  async getAdminWorkerAttendance({ branchId = null, artistId = null, dateFrom = null, dateTo = null } = {}) {
    const params = new URLSearchParams();
    if (branchId != null) params.append('branch_id', branchId);
    if (artistId != null) params.append('artist_id', artistId);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    return this.request(`/admin/attendance/workers?${params.toString()}`);
  },
  async createAdminWorkerAttendance(data) {
    return this.request('/admin/attendance/workers', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateAdminWorkerAttendance(id, data) {
    return this.request(`/admin/attendance/workers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminWorkerAttendance(id) {
    return this.request(`/admin/attendance/workers/${id}`, { method: 'DELETE' });
  },
  async getAdminBranchManagerAttendance({ branchId = null, dateFrom = null, dateTo = null } = {}) {
    const params = new URLSearchParams();
    if (branchId != null) params.append('branch_id', branchId);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    return this.request(`/admin/attendance/branch-managers?${params.toString()}`);
  },
  async createAdminBranchManagerAttendance(data) {
    return this.request('/admin/attendance/branch-managers', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateAdminBranchManagerAttendance(id, data) {
    return this.request(`/admin/attendance/branch-managers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminBranchManagerAttendance(id) {
    return this.request(`/admin/attendance/branch-managers/${id}`, { method: 'DELETE' });
  },
  async getAdminAttendanceByBranch({ shopId = null, dateFrom = null, dateTo = null } = {}) {
    const params = new URLSearchParams();
    if (shopId != null) params.append('shop_id', shopId);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    return this.request(`/admin/attendance/by-branch?${params.toString()}`);
  },
  async getAdminActiveVips({ shopId = null, branchId = null } = {}) {
    const params = new URLSearchParams();
    if (shopId != null) params.append('shop_id', shopId);
    if (branchId != null) params.append('branch_id', branchId);
    const qs = params.toString();
    return this.request(qs ? `/admin/active-vips?${qs}` : '/admin/active-vips');
  },
  async updateAdminPremiumBookingStatus(bookingId, status) {
    return this.request(`/admin/premium-bookings/${bookingId}/status?status=${encodeURIComponent(status)}`, { method: 'PUT' });
  },
  async getAdminArtistBuddies(branchId = null) {
    const q = branchId != null ? `?branch_id=${branchId}` : '';
    return this.request(`/admin/artist-buddies${q}`);
  },
  async createAdminArtistBuddy(artistId, buddyArtistId, branchId) {
    const params = new URLSearchParams({
      artist_id: artistId,
      buddy_artist_id: buddyArtistId,
      branch_id: branchId,
    });
    return this.request(`/admin/artist-buddies?${params}`, { method: 'POST' });
  },
  async deleteAdminArtistBuddy(buddyId) {
    return this.request(`/admin/artist-buddies/${buddyId}`, { method: 'DELETE' });
  },
  async getAdminPremiumSettings(shopId = null) {
    const q = shopId != null ? `?shop_id=${shopId}` : '';
    return this.request(`/admin/premium-settings${q}`);
  },
  async updateAdminPremiumSettings({ shopId, monthly_price, max_members }) {
    const params = new URLSearchParams({ shop_id: shopId });
    if (monthly_price !== undefined && monthly_price !== null) params.append('monthly_price', monthly_price);
    if (max_members !== undefined && max_members !== null) params.append('max_members', max_members);
    return this.request(`/admin/premium-settings?${params}`, { method: 'PUT' });
  },
  async getAdminProductCategories(branchId = null) {
    const q = branchId != null ? `?branch_id=${branchId}` : '';
    return this.request(`/admin/product-categories${q}`);
  },
  async getAdminProductCategory(id) {
    return this.request(`/admin/product-categories/${id}`);
  },
  async createAdminProductCategory(data) {
    return this.request('/admin/product-categories', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateAdminProductCategory(id, data) {
    return this.request(`/admin/product-categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminProductCategory(id) {
    return this.request(`/admin/product-categories/${id}`, { method: 'DELETE' });
  },

  async getAdminProducts(branchId = null) {
    const url = branchId != null ? `/admin/products?branch_id=${branchId}` : '/admin/products';
    return this.request(url);
  },
  async createAdminProduct(data) {
    return this.request('/admin/products', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateAdminProduct(id, data) {
    return this.request(`/admin/products/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminProduct(id) {
    return this.request(`/admin/products/${id}`, { method: 'DELETE' });
  },
  async getAdminTimeSlots(branchId = null) {
    const url = branchId != null ? `/admin/time-slots?branch_id=${branchId}` : '/admin/time-slots';
    return this.request(url);
  },
  async createAdminTimeSlot(data) {
    return this.request('/admin/time-slots', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateAdminTimeSlot(id, data) {
    return this.request(`/admin/time-slots/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminTimeSlot(id) {
    return this.request(`/admin/time-slots/${id}`, { method: 'DELETE' });
  },
  async getAdminCategories(branchId) {
    const url = branchId != null ? `/admin/categories?branch_id=${branchId}` : '/admin/categories';
    return this.request(url);
  },
  async createAdminCategory(data) {
    return this.request('/admin/categories', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateAdminCategory(id, data) {
    return this.request(`/admin/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminCategory(id) {
    return this.request(`/admin/categories/${id}`, { method: 'DELETE' });
  },
  async getAdminServices(shopId) {
    const url = shopId != null ? `/admin/services?shop_id=${shopId}` : '/admin/services';
    return this.request(url);
  },
  async createAdminService(data) {
    return this.request('/admin/services', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateAdminService(id, data) {
    return this.request(`/admin/services/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminService(id) {
    return this.request(`/admin/services/${id}`, { method: 'DELETE' });
  },
  async getAdminBundles(shopId = null, branchId = null) {
    const params = new URLSearchParams();
    if (shopId != null) params.append('shop_id', shopId);
    if (branchId != null) params.append('branch_id', branchId);
    const qs = params.toString();
    return this.request(qs ? `/admin/bundles?${qs}` : '/admin/bundles');
  },
  async createAdminBundle(data) {
    return this.request('/admin/bundles', { method: 'POST', body: JSON.stringify(data) });
  },
  async getAdminBundle(id) {
    return this.request(`/admin/bundles/${id}`);
  },
  async updateAdminBundle(id, data) {
    return this.request(`/admin/bundles/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminBundle(id) {
    return this.request(`/admin/bundles/${id}`, { method: 'DELETE' });
  },
  async getAdminSeats(shopId) {
    const url = shopId != null ? `/admin/seats?shop_id=${shopId}` : '/admin/seats';
    return this.request(url);
  },
  async createAdminSeat(data) {
    return this.request('/admin/seats', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateAdminSeat(id, data) {
    return this.request(`/admin/seats/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteAdminSeat(id) {
    return this.request(`/admin/seats/${id}`, { method: 'DELETE' });
  },

  // Admin Orders
  async getAdminOrders(params = {}) {
    const p = new URLSearchParams();
    if (params.branch_id) p.append('branch_id', params.branch_id);
    if (params.status) p.append('status', params.status);
    if (params.skip != null) p.append('skip', params.skip);
    if (params.limit != null) p.append('limit', params.limit);
    const qs = p.toString();
    return this.request(qs ? `/admin/orders?${qs}` : '/admin/orders');
  },
  async getAdminOrder(id) {
    return this.request(`/admin/orders/${id}`);
  },
  async updateAdminOrder(id, data) {
    return this.request(`/admin/orders/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async getAdminArtistServices(artistId) {
    return this.request(`/admin/artists/${artistId}/services`);
  },
  async updateAdminArtistServices(artistId, data) {
    return this.request(`/admin/artists/${artistId}/services`, { method: 'PUT', body: JSON.stringify(data) });
  },

  // Manager (salon_manager)
  async getManagerDashboard() {
    return this.request('/manager/dashboard');
  },
  async getManagerArtists() {
    return this.request('/manager/artists');
  },
  async createManagerArtist(data) {
    return this.request('/manager/artists', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateManagerArtist(id, data) {
    return this.request(`/manager/artists/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteManagerArtist(id) {
    return this.request(`/manager/artists/${id}`, { method: 'DELETE' });
  },
  async getManagerProducts() {
    return this.request('/manager/products');
  },
  async createManagerProduct(data) {
    return this.request('/manager/products', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateManagerProduct(id, data) {
    return this.request(`/manager/products/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteManagerProduct(id) {
    return this.request(`/manager/products/${id}`, { method: 'DELETE' });
  },
  async uploadManagerProductPhoto(productId, formData) {
    const response = await fetch(`${API_URL}/manager/products/${productId}/photo`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to upload photo' }));
      throw new Error(error.detail || 'Failed to upload photo');
    }
    return response.json();
  },
  async deleteManagerProductPhoto(productId) {
    const response = await fetch(`${API_URL}/manager/products/${productId}/photo`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to delete photo' }));
      throw new Error(error.detail || 'Failed to delete photo');
    }
    return response.json();
  },
  async getManagerProductCategories(branchId) {
    const url = branchId != null ? `/manager/product-categories?branch_id=${branchId}` : '/manager/product-categories';
    return this.request(url);
  },
  async createManagerProductCategory(data) {
    return this.request('/manager/product-categories', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateManagerProductCategory(id, data) {
    return this.request(`/manager/product-categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteManagerProductCategory(id) {
    return this.request(`/manager/product-categories/${id}`, { method: 'DELETE' });
  },
  async getManagerBookings() {
    return this.request('/manager/bookings');
  },
  async getManagerBranches() {
    return this.request('/manager/branches');
  },
  async getManagerCategories(branchId) {
    const url = branchId != null ? `/manager/categories?branch_id=${branchId}` : '/manager/categories';
    return this.request(url);
  },
  async createManagerCategory(data) {
    return this.request('/manager/categories', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateManagerCategory(id, data) {
    return this.request(`/manager/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteManagerCategory(id) {
    return this.request(`/manager/categories/${id}`, { method: 'DELETE' });
  },
  async createManagerBooking(data) {
    return this.request('/manager/bookings', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateManagerBooking(id, statusData) {
    return this.request(`/manager/bookings/${id}`, { method: 'PUT', body: JSON.stringify(statusData) });
  },
  async getManagerAnalytics(branchId = null) {
    const url = branchId != null ? `/manager/analytics?branch_id=${branchId}` : '/manager/analytics';
    return this.request(url);
  },
  async downloadManagerFinancialReport(branchId = null) {
    const url = `${API_URL}/manager/financial-report/csv${branchId != null ? `?branch_id=${branchId}` : ''}`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(formatApiError(err.detail, 'Failed to download report'));
    }
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition');
    const match = cd?.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] || `financial_report_${new Date().toISOString().slice(0, 10)}.csv`;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  },
  async getManagerFinancialReport(dateFrom, dateTo, branchId = null) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    if (branchId) params.append('branch_id', branchId);
    const qs = params.toString();
    return this.request(`/manager/financial-report${qs ? `?${qs}` : ''}`);
  },
  async getManagerServices() {
    return this.request('/manager/services');
  },
  async createManagerService(data) {
    return this.request('/manager/services', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateManagerService(id, data) {
    return this.request(`/manager/services/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteManagerService(id) {
    const endpoint = `/manager/services/${id}`;
    try {
      return await this.request(endpoint, { method: 'DELETE' });
    } catch (err) {
      // Some production proxies block DELETE and surface a network error.
      if (err instanceof TypeError) {
        return this.request(`${endpoint}/delete`, { method: 'POST' });
      }
      throw err;
    }
  },
  async getManagerBundles() {
    return this.request('/manager/bundles');
  },
  async createManagerBundle(data) {
    return this.request('/manager/bundles', { method: 'POST', body: JSON.stringify(data) });
  },
  async getManagerBundle(id) {
    return this.request(`/manager/bundles/${id}`);
  },
  async updateManagerBundle(id, data) {
    return this.request(`/manager/bundles/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteManagerBundle(id) {
    return this.request(`/manager/bundles/${id}`, { method: 'DELETE' });
  },
  async getManagerSeats() {
    return this.request('/manager/seats');
  },
  async createManagerSeat(data) {
    return this.request('/manager/seats', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateManagerSeat(id, data) {
    return this.request(`/manager/seats/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteManagerSeat(id) {
    return this.request(`/manager/seats/${id}`, { method: 'DELETE' });
  },
  async getAssignableBranchManagers() {
    return this.request('/manager/assignable-branch-managers');
  },
  async getManagerArtistServices(artistId) {
    return this.request(`/manager/artists/${artistId}/services`);
  },
  async updateManagerArtistServices(artistId, data) {
    return this.request(`/manager/artists/${artistId}/services`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async uploadManagerArtistPhoto(artistId, formData) {
    const response = await fetch(`${API_URL}/manager/artists/${artistId}/photo`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to upload photo' }));
      throw new Error(error.detail || 'Failed to upload photo');
    }
    return response.json();
  },
  async deleteManagerArtistPhoto(artistId) {
    const response = await fetch(`${API_URL}/manager/artists/${artistId}/photo`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to delete photo' }));
      throw new Error(error.detail || 'Failed to delete photo');
    }
    return response.json();
  },

  // Attendance
  async getManagerWorkerAttendance(branchId = null, dateFrom = null, dateTo = null) {
    const params = new URLSearchParams();
    if (branchId) params.append('branch_id', branchId);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    return this.request(`/manager/attendance/workers?${params.toString()}`);
  },
  async createManagerWorkerAttendance(data) {
    return this.request('/manager/attendance/workers', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateManagerWorkerAttendance(id, data) {
    return this.request(`/manager/attendance/workers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteManagerWorkerAttendance(id) {
    return this.request(`/manager/attendance/workers/${id}`, { method: 'DELETE' });
  },
  async getManagerBranchManagerAttendance(branchId = null, dateFrom = null, dateTo = null) {
    const params = new URLSearchParams();
    if (branchId) params.append('branch_id', branchId);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    return this.request(`/manager/attendance/branch-managers?${params.toString()}`);
  },
  async createManagerBranchManagerAttendance(data) {
    return this.request('/manager/attendance/branch-managers', { method: 'POST', body: JSON.stringify(data) });
  },
  async updateManagerBranchManagerAttendance(id, data) {
    return this.request(`/manager/attendance/branch-managers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  },
  async deleteManagerBranchManagerAttendance(id) {
    return this.request(`/manager/attendance/branch-managers/${id}`, { method: 'DELETE' });
  },
  async getManagerAttendanceByBranch(dateFrom = null, dateTo = null) {
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);
    return this.request(`/manager/attendance/by-branch?${params.toString()}`);
  },

  // Blocked slots (admin)
  async getBlockedSlots(slotConfigId) {
    return this.request(`/admin/blocked-slots?slot_config_id=${slotConfigId}`);
  },
  async createBlockedSlot(data) {
    return this.request('/admin/blocked-slots', { method: 'POST', body: JSON.stringify(data) });
  },
  async deleteBlockedSlot(id) {
    return this.request(`/admin/blocked-slots/${id}`, { method: 'DELETE' });
  },

  // Closed Dates (Branch)
  async getBranchClosedDates(branchId) {
    return this.request(`/branches/${branchId}/closed-dates`);
  },
  async createClosedDate(branchId, data) {
    return this.request(`/branches/${branchId}/closed-dates`, { method: 'POST', body: JSON.stringify(data) });
  },
  async deleteClosedDate(id) {
    return this.request(`/branches/closed-dates/${id}`, { method: 'DELETE' });
  },

  // Booking/QR Validation
  async verifyQR(reservationId) {
    return this.request('/bookings/verify-qr', {
      method: 'POST',
      body: JSON.stringify({ reservation_id: reservationId }),
    });
  },

  // ==========================================
  // Premium VIP Membership
  // ==========================================

  // Premium Client
  async getPremiumHome() {
    return this.request('/premium/home');
  },
  async getPremiumShops() {
    return this.request('/premium/shops');
  },
  async getMyMemberships() {
    return this.request('/premium/memberships');
  },
  async requestPremiumMembership(shopId) {
    return this.request('/premium/memberships/request', {
      method: 'POST',
      body: JSON.stringify({ shop_id: shopId }),
    });
  },
  async getPremiumBookings(status = null) {
    const params = status ? `?status=${status}` : '';
    return this.request(`/premium/bookings${params}`);
  },
  async getPremiumBooking(id) {
    return this.request(`/premium/bookings/${id}`);
  },
  async createPremiumBooking(data) {
    return this.request('/premium/bookings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  async triggerOnMyWay(bookingId, etaMinutes) {
    return this.request(`/premium/bookings/${bookingId}/on-my-way`, {
      method: 'POST',
      body: JSON.stringify({ eta_minutes: etaMinutes }),
    });
  },
  async updateETA(bookingId, etaMinutes) {
    return this.request(`/premium/bookings/${bookingId}/update-eta`, {
      method: 'PUT',
      body: JSON.stringify({ eta_minutes: etaMinutes }),
    });
  },
  async cancelOnMyWay(bookingId) {
    return this.request(`/premium/bookings/${bookingId}/cancel-eta`, { method: 'DELETE' });
  },
  async premiumCheckIn(bookingId) {
    return this.request(`/premium/bookings/${bookingId}/arrived`, { method: 'POST' });
  },
  async ratePremiumBooking(bookingId, rating) {
    return this.request(`/premium/bookings/${bookingId}/rating?rating=${rating}`, { method: 'PUT' });
  },
  async cancelPremiumBooking(bookingId) {
    return this.request(`/premium/bookings/${bookingId}/cancel`, { method: 'PUT' });
  },
  async getPremiumNotifications() {
    return this.request('/premium/notifications');
  },
  async markPremiumNotificationRead(id) {
    return this.request(`/premium/notifications/${id}/read`, { method: 'PUT' });
  },

  // Admin Premium
  async getAdminPremiumPrivileges() {
    return this.request('/admin/premium-privileges');
  },
  async createAdminPremiumPrivilege(shopId, monthlyPrice, maxMembers = null) {
    const params = new URLSearchParams({ shop_id: shopId, monthly_price: monthlyPrice });
    if (maxMembers) params.append('max_members', maxMembers);
    return this.request(`/admin/premium-privileges?${params}`, { method: 'POST' });
  },
  async updateAdminPremiumPrivilege(id, data) {
    const params = new URLSearchParams();
    if (data.enabled !== undefined) params.append('enabled', data.enabled);
    if (data.monthly_price !== undefined) params.append('monthly_price', data.monthly_price);
    if (data.max_members !== undefined) params.append('max_members', data.max_members);
    return this.request(`/admin/premium-privileges/${id}?${params}`, { method: 'PUT' });
  },
  async getAdminPremiumMemberships(status = null, shopId = null) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (shopId) params.append('shop_id', shopId);
    return this.request(`/admin/premium-memberships?${params}`);
  },
  async adminApproveMembership(id, action, reason = null) {
    const params = new URLSearchParams({ action });
    if (reason) params.append('rejection_reason', reason);
    return this.request(`/admin/premium-memberships/${id}?${params}`, { method: 'PUT' });
  },
  async getAdminPremiumBookings(shopId = null, status = null) {
    const params = new URLSearchParams();
    if (shopId) params.append('shop_id', shopId);
    if (status) params.append('status', status);
    return this.request(`/admin/premium-bookings?${params}`);
  },

  // Manager Premium
  async getManagerPremiumMemberships(status = null) {
    const params = status ? `?status=${status}` : '';
    return this.request(`/manager/premium-memberships${params}`);
  },
  async managerApproveMembership(id, action, reason = null) {
    const params = new URLSearchParams({ action });
    if (reason) params.append('rejection_reason', reason);
    return this.request(`/manager/premium-memberships/${id}?${params}`, { method: 'PUT' });
  },
  async getManagerPremiumBookings(status = null, branchId = null) {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (branchId) params.append('branch_id', branchId);
    return this.request(`/manager/premium-bookings?${params}`);
  },
  async getManagerActiveVips() {
    return this.request('/manager/active-vips');
  },
  async updateManagerPremiumBookingStatus(bookingId, status) {
    return this.request(`/manager/premium-bookings/${bookingId}/status?status=${status}`, { method: 'PUT' });
  },
  async getManagerArtistBuddies(branchId = null) {
    const params = branchId ? `?branch_id=${branchId}` : '';
    return this.request(`/manager/artist-buddies${params}`);
  },
  async createManagerArtistBuddy(artistId, buddyArtistId, branchId) {
    const params = new URLSearchParams({ artist_id: artistId, buddy_artist_id: buddyArtistId, branch_id: branchId });
    return this.request(`/manager/artist-buddies?${params}`, { method: 'POST' });
  },
  async deleteManagerArtistBuddy(id) {
    return this.request(`/manager/artist-buddies/${id}`, { method: 'DELETE' });
  },
  async getManagerPremiumSettings() {
    return this.request('/manager/premium-settings');
  },
  async updateManagerPremiumSettings(data) {
    const params = new URLSearchParams();
    if (data.monthly_price !== undefined) params.append('monthly_price', data.monthly_price);
    if (data.max_members !== undefined) params.append('max_members', data.max_members);
    return this.request(`/manager/premium-settings?${params}`, { method: 'PUT' });
  },

  // Worker endpoints
  async getWorkerProfile() {
    return this.request('/worker/me');
  },
  async getWorkerToday() {
    return this.request('/worker/today');
  },
  async getWorkerSchedule(startDate, endDate) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    return this.request(`/worker/schedule?${params}`);
  },
  async updateWorkerBookingStatus(bookingId, status) {
    return this.request(`/worker/booking/${bookingId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },

  // Optimization — utilization heatmap
  async getAdminUtilizationHeatmap(branchId, days = 60) {
    return this.request(`/admin/utilization-heatmap?branch_id=${branchId}&days=${days}`);
  },
  async getManagerUtilizationHeatmap(branchId, days = 60) {
    const q = new URLSearchParams({ days });
    if (branchId) q.append('branch_id', branchId);
    return this.request(`/manager/utilization-heatmap?${q}`);
  },
};
